/* eslint-disable no-nested-ternary */
// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { FormProvider, useForm } from 'react-hook-form';
import { IRecordField, IRecordHeaders } from 'ui-component/records/types';
import { getViewFields, getAditionalFieldsValues } from './utils';
import EditPanel, { CreatePanelForm, EditPanelForm } from './EditPanel/v2';
import ViewPanel from './ViewPanel';
import { LogReportPanel } from './LogReportPanel';
import { IFormatDataValues } from '../types';
import { generateInitialState, getDataToCreate, getDataToUpdate } from '../utils';
import useMyRole from 'hooks/useMyRole';
import { Box } from '@mui/material';
import { filterNotAllowedFields } from 'ui-component/records/utils';
import { generateClearedRecordData, splitHeaders } from 'ui-component/records/utils/headerHelpers';
import { useDispatch } from 'store';
import { openDrawer } from 'store/slices/menu';
import { FindObjectProperty, FindObjectPropertyVariables, ObjectProperty } from 'views/backoffice/CustomObjects/types';
import { QUERY_GET_OBJECT_PROPERTIES } from 'graphql/queries/customObjects';
import ObjectPanelView from 'ui-component/records/RecordGrid/components/ObjectPanelView';
import ObjectPanelEditForm from 'ui-component/records/RecordGrid/components/ObjectPanelEditForm';
import { useConfirmationModalContext } from 'hooks/useConfirmationModal';
import { DROPDOWN_COLUMNS } from 'ui-component/records/utils/columnHelpers';
import { useRecordData } from './hooks';
import SideBar from './SideBar';
import FooterOptions from './Footer';

interface PropertiesPanelProps {
    loading?: boolean;
    recordData?: IRecordHeaders | null;
    recordType?: string;
    isCreate?: boolean;
    hasFile?: boolean;
    onSubmit: (recordType: number, data: IFormatDataValues) => Promise<boolean>;
    isEditMode?: any;
    onUploadFile: () => void;
    onToggleLineItems?: () => void;
    fileAdded?: boolean;
    onToggleComments?: () => void;
    onToggleAttachments?: () => void;
}
export interface IForm {
    recordType?: number;
    recordNumber: string;
    statusId: number | null;
    problemCodeId: number | null;
    approvedBy: number | null;
    recordDate: string | null;
    dueDate: string | null;
    poNumber: string;
    assignedTo: number | null;
}

// @deprecated now us
export const enum PanelTitle {
    Edit = 'Edit',
    LogReport = 'Log report',
    View = 'View',
    ViewObject = 'ViewObject',
    EditObject = 'EditObject'
}

export const PropertiesPanelv2 = ({
    loading,
    recordData = null,
    recordType,
    isCreate,
    onSubmit,
    onUploadFile,
    hasFile = false,
    isEditMode,
    onToggleLineItems,
    fileAdded = false,
    onToggleComments,
    onToggleAttachments
}: PropertiesPanelProps) => {
    const modal = useConfirmationModalContext();
    const roleId = useMyRole();
    const dispatch = useDispatch();
    // Objects States
    const [objectProperties, setObjectProperties] = useState<(ObjectProperty & { value: string })[]>([]);
    const [selectedObjectToEdit, setSelectedObjectToEdit] = useState<{ id: number; name: string } | null>(null);
    const [inMemoryObjectProperties, setInMemoryObjectProperties] = useState<{ [key: string]: any }>({});
    const [prevChangePanel, setPrevChangePanel] = useState<PanelTitle>(PanelTitle.View);
    const [isAllowedToEditObject, setIsAllowedToEditObject] = useState(true);
    // Objects Queries
    const [getObjectProperties, { loading: loadingObjectProperties }] = useLazyQuery<FindObjectProperty, FindObjectPropertyVariables>(
        QUERY_GET_OBJECT_PROPERTIES
    );
    // Objects Control
    const handleOpenViewObjectPanel = async (field: IRecordField, fieldName: string, editable: boolean) => {
        const objectData = recordData?.additionalFields?.find((additionalField) => {
            const fieldId = additionalField.tag.split(';')[2];
            return Number(fieldId) === Number(field.id);
        });
        setSelectedObjectToEdit({ id: Number(objectData?.id), name: fieldName });
        const { data: objectPropertiesData } = await getObjectProperties({
            variables: {
                data: {
                    objectDefinitionIds: field.objectDefinition.id
                }
            }
        });
        if (!isCreate) {
            let mappedProperties = [];
            if (!inMemoryObjectProperties[fieldName]) {
                mappedProperties =
                    objectPropertiesData?.findObjectProperty
                        .map((property) => ({
                            ...property,
                            value:
                                objectData?.objectValue.objectValues?.find((el) => Number(el.objectProperty.id) === Number(property.id))
                                    ?.value || ''
                        }))
                        .filter((property) => property.enabled)
                        .sort((a, b) => {
                            const orderA = a.order; // ignore upper and lowercase
                            const orderB = b.order; // ignore upper and lowercase
                            if (orderA < orderB) {
                                return -1;
                            }
                            if (orderA > orderB) {
                                return 1;
                            }
                            return 0;
                        }) || [];
            } else {
                mappedProperties = inMemoryObjectProperties[fieldName];
            }
            setObjectProperties(mappedProperties || []);
        } else if (isCreate) {
            const mappedProperties = objectPropertiesData?.findObjectProperty
                .map((property) => ({
                    ...property,
                    value: ''
                }))
                .sort((a, b) => {
                    const orderA = a.order;
                    const orderB = b.order;
                    if (orderA < orderB) {
                        return -1;
                    }
                    if (orderA > orderB) {
                        return 1;
                    }
                    return 0;
                });
            setObjectProperties(inMemoryObjectProperties[fieldName] || mappedProperties);
        } else {
            setObjectProperties([]);
        }
        setIsAllowedToEditObject(editable);
        setPrevChangePanel(changePanel);
        setChangePanel(PanelTitle.ViewObject);
    };

    const handleUpdateObject = async (newProperties: (ObjectProperty & { value: string })[]) => {
        const stateInsert: { [key: string]: any } = {};
        stateInsert[String(selectedObjectToEdit?.name)] = newProperties;
        setInMemoryObjectProperties((prev) => ({ ...prev, ...stateInsert }));
        setPrevChangePanel(PanelTitle.View);
        setChangePanel(PanelTitle.Edit);
    };

    const [changePanel, setChangePanel] = useState<PanelTitle>(isCreate ? PanelTitle.Edit : PanelTitle.View);
    const [showFullHistory, setShowFullHistory] = useState(false);
    const [recordTypeId, setRecordTypeId] = useState(recordType);
    const [initialFormState, setInitialFormState] = useState<Partial<CreatePanelForm>>({});

    const methods = useForm<CreatePanelForm | EditPanelForm>({
        mode: 'onChange'
    });

    const { fieldAccess: fieldAccessList, headers: headersData } = useRecordData(recordTypeId);

    // Headers
    const checkAllowedFields = useMemo(
        () => filterNotAllowedFields(fieldAccessList, Number(recordTypeId), roleId),
        [fieldAccessList, recordTypeId, roleId]
    );

    const { baseFieldHeaders, additionalFieldHeaders } = useMemo(
        () => splitHeaders(headersData, checkAllowedFields),
        [checkAllowedFields, headersData]
    );

    // Values
    const aditionalFields = useMemo(
        () =>
            additionalFieldHeaders && recordData?.additionalFields
                ? getAditionalFieldsValues(additionalFieldHeaders, recordData?.additionalFields)
                : {},
        [additionalFieldHeaders, recordData?.additionalFields]
    );

    // Fields for view Panel
    const viewFields = useMemo(
        () => getViewFields(recordData, baseFieldHeaders, additionalFieldHeaders, fieldAccessList),
        [additionalFieldHeaders, baseFieldHeaders, recordData, fieldAccessList]
    );

    const toggleEditForm = () => {
        setChangePanel((s) => (s === PanelTitle.Edit ? PanelTitle.View : PanelTitle.Edit));
    };

    const toggleLogReport = () => {
        setChangePanel((s) => (s === PanelTitle.LogReport ? PanelTitle.View : PanelTitle.LogReport));
    };

    const handleClickCancel = async () => {
        const formData = methods.getValues();
        const clearedData = generateClearedRecordData(initialFormState, formData, {});
        if (Object.entries(clearedData).filter(([_key, val]) => val !== null).length === 0) setChangePanel(PanelTitle.View);
        else
            await modal.showConfirmation({
                title: 'Are you sure you want to discard your changes?',
                content: '',
                loadingText: 'Saving Changes',
                forwardButtonText: 'Discard Changes',
                actionButtonText: 'Save changes',
                onForward: async () => setChangePanel(PanelTitle.View),
                onAction: handleSubmit,
                showCheckbox: true,
                actionType: 'discard',
                sectionTitle: 'record_viewer',
                recordType: '',
                hideCloseBtn: true
            });
    };

    // Submit handlers
    const handleClickSave = async () => {
        if (await methods.trigger()) handleShowConfirmationDialog();
    };

    const handleShowConfirmationDialog = async () => {
        await modal.showConfirmation({
            title: hasFile ? 'Do you want to save this changes?' : "This record doesn't contain a document",
            content: '',
            loadingText: 'Saving Changes',
            forwardButtonText: hasFile ? 'Save changes' : 'Save Anyway',
            actionButtonText: hasFile ? 'Discard Changes' : 'Attach a Document',
            onForward: handleSubmit,
            onAction: hasFile ? async () => setChangePanel(PanelTitle.View) : onUploadFile,
            showCheckbox: true,
            actionType: isCreate ? 'create' : 'edit',
            sectionTitle: 'record_viewer_save',
            recordType: '',
            hideCloseBtn: true
        });
    };

    const handleSubmit = async () => {
        const formData = { ...methods.getValues(), ...inMemoryObjectProperties };
        const clearedData = generateClearedRecordData(initialFormState, formData, {});

        if (!isCreate && Object.entries(clearedData).filter(([_key, val]) => val !== null).length === 0 && !fileAdded) {
            setChangePanel(PanelTitle.View);
            return;
        }

        const res = await onSubmit(
            Number(recordTypeId),
            isCreate
                ? getDataToCreate(formData, baseFieldHeaders, additionalFieldHeaders, fieldAccessList)
                : getDataToUpdate(
                      clearedData,
                      baseFieldHeaders,
                      additionalFieldHeaders,
                      recordData?.additionalFields || [],
                      fieldAccessList
                  )
        );
        if (res) setChangePanel(PanelTitle.View);
    };

    const handleSaveEditObjectFormActions = async (values: { [key: string]: string }, modalTitle: string) => {
        await modal.showConfirmation({
            title: modalTitle,
            content: '',
            loadingText: 'Saving Changes',
            forwardButtonText: 'Save changes',
            actionButtonText: 'Discard Changes',
            onForward: async () => {
                const newObjectProperties = objectProperties.map((item: ObjectProperty & { value: string }) => ({
                    ...item,
                    value: values[item.name]
                }));
                setObjectProperties(newObjectProperties);
                handleUpdateObject(newObjectProperties);
                if (!isCreate) {
                    const newProperties: { [key: string]: any } = {};
                    newProperties[String(selectedObjectToEdit?.name)] = newObjectProperties;
                    const formData = { ...methods.getValues(), ...inMemoryObjectProperties, ...newProperties };
                    const clearedData = generateClearedRecordData(initialFormState, formData, {});
                    await onSubmit(
                        Number(recordTypeId),
                        getDataToUpdate(
                            clearedData,
                            baseFieldHeaders,
                            additionalFieldHeaders,
                            recordData?.additionalFields || [],
                            fieldAccessList
                        )
                    );
                }
            },
            onAction: () => setChangePanel(prevChangePanel),
            showCheckbox: true,
            actionType: `${modalTitle === 'Do you want to save this changes?' ? `save` : `discard`}_object_changes`,
            sectionTitle: 'record_viewer',
            recordType: '',
            hideCloseBtn: true
        });
    };

    const loadingFields = loadingHeaderData;

    // create form for edit record
    useEffect(() => {
        if (!recordData || !additionalFieldHeaders || !baseFieldHeaders || isCreate) return;

        const formState = Object.keys(baseFieldHeaders)
            .sort()
            .reduce((acc, key) => {
                const value =
                    ['string', 'number', 'boolean', 'date'].includes(baseFieldHeaders[key]?.dataType || '') &&
                    !DROPDOWN_COLUMNS.includes(key as (typeof DROPDOWN_COLUMNS)[number])
                        ? recordData[key as keyof IRecordHeaders]
                        : (recordData[key as keyof IRecordHeaders] as Record<string, any>)?.id;
                return { ...acc, [key]: value };
            }, {});
        setInitialFormState({
            ...formState,
            ...(aditionalFields || {})
        });
        methods.reset({
            ...formState,
            ...(aditionalFields || {})
        });
    }, [aditionalFields, additionalFieldHeaders, recordData, recordType, baseFieldHeaders, isCreate, viewFields, methods]);

    // create form for new record
    useEffect(() => {
        if (isCreate && baseFieldHeaders && changePanel === PanelTitle.Edit) {
            const initialState = generateInitialState(baseFieldHeaders, recordType);
            setInitialFormState(initialState);
            if (!methods.formState.defaultValues) methods.reset(initialState);
        }
    }, [baseFieldHeaders, changePanel, isCreate, methods, recordType]);

    useEffect(() => {
        if (!isEditMode) return;
        if (changePanel === PanelTitle.Edit) isEditMode(true);
        else isEditMode(false);
    }, [changePanel, isEditMode]);

    return (
        <div
            data-testid="properties-panel"
            style={{
                height: '100%',
                display: changePanel === PanelTitle.ViewObject || changePanel === PanelTitle.EditObject ? 'flex' : 'block',
                flexDirection: 'column'
            }}
        >
            <FormProvider {...methods}>
                <SideBar
                    isCreate={isCreate}
                    title={
                        changePanel === PanelTitle.LogReport
                            ? 'Record History'
                            : changePanel === PanelTitle.ViewObject || changePanel === PanelTitle.EditObject
                            ? `${selectedObjectToEdit?.name ?? 'Object'}`
                            : 'Details'
                    }
                    changePanel={changePanel}
                    isOpenForm={changePanel === PanelTitle.Edit}
                    toggleEditForm={toggleEditForm}
                    onShowFullHistory={() => {
                        setShowFullHistory(true);
                        dispatch(openDrawer(false));
                    }}
                    showHistory={changePanel === PanelTitle.LogReport}
                    toggleLogReport={toggleLogReport}
                    onToggleLineItems={onToggleLineItems}
                    isObjectPanel={changePanel === PanelTitle.ViewObject || changePanel === PanelTitle.EditObject}
                    onGoBack={() => {
                        setPrevChangePanel(PanelTitle.View);
                        setChangePanel(prevChangePanel);
                    }}
                    onToggleComments={onToggleComments}
                    onToggleAttachments={onToggleAttachments}
                />
                {/* </Box> */}
                <Box
                    sx={{
                        height:
                            changePanel === PanelTitle.ViewObject || changePanel === PanelTitle.EditObject
                                ? 'calc(100% - 60px)'
                                : 'calc(100% - 125px)',
                        '& > *:first-child': { overflowY: 'auto', height: '100%' }
                    }}
                >
                    {changePanel === PanelTitle.View && (
                        <ViewPanel
                            fields={viewFields}
                            loading={loadingFieldAccess || loading || loadingHeaderData}
                            headerData={headerData?.getSaasRecordFieldsByRecordTypes || null}
                            handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                        />
                    )}
                    {changePanel === PanelTitle.ViewObject && (
                        <ObjectPanelView
                            objectName={`${selectedObjectToEdit?.name}`}
                            loadingObjectProperties={loadingObjectProperties}
                            handleClosePanel={() => {
                                setChangePanel(PanelTitle.View);
                                setObjectProperties([]);
                            }}
                            handleOpenEditPanel={() => {
                                setChangePanel(PanelTitle.EditObject);
                            }}
                            objectProperties={objectProperties}
                            isRecordViewer
                            renderEditButton={isAllowedToEditObject}
                        />
                    )}
                    {changePanel === PanelTitle.EditObject && (
                        <ObjectPanelEditForm
                            objectName={`${selectedObjectToEdit?.name}`}
                            showTopCloseButton
                            objectProperties={objectProperties}
                            handleUpdateObjectViewer={handleSaveEditObjectFormActions}
                            handleClosePanelViewer={handleSaveEditObjectFormActions}
                            isRecordViewer
                        />
                    )}
                    {(changePanel === PanelTitle.Edit ||
                        changePanel === PanelTitle.EditObject ||
                        changePanel === PanelTitle.ViewObject) && (
                        <EditPanel
                            isCreate={isCreate}
                            recordTypeId={recordType}
                            onChangeRecordType={(val) => setRecordTypeId(val)}
                            baseFieldHeaders={baseFieldHeaders}
                            aditionalFieldHeader={additionalFieldHeaders}
                            loadingFields={loadingFields}
                            fieldAccessList={fieldAccessList}
                            recordData={recordData}
                            inMemoryObjectProperties={inMemoryObjectProperties}
                            handleOpenViewObjectPanel={handleOpenViewObjectPanel}
                            isObjectPanelOpen={changePanel === PanelTitle.EditObject || changePanel === PanelTitle.ViewObject}
                        />
                    )}

                    {changePanel === PanelTitle.LogReport && (
                        <LogReportPanel
                            recordId={Number(recordData?.id)}
                            showFullHistory={showFullHistory}
                            onCloseFullHistory={() => setShowFullHistory(false)}
                        />
                    )}
                </Box>
                {!(changePanel === PanelTitle.ViewObject || changePanel === PanelTitle.EditObject) && (
                    <FooterOptions
                        panelState={changePanel}
                        onEdit={toggleEditForm}
                        onSave={handleClickSave}
                        onCancel={handleClickCancel}
                        isCreate={isCreate}
                        disabledButtons={loadingFieldAccess || loading || loadingHeaderData}
                    />
                )}
            </FormProvider>
        </div>
    );
};

export default PropertiesPanelv2;
